import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip
} from "@material-ui/core";
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Visibility,
  MonetizationOn,
  TrendingUp,
  People
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  tableContainer: {
    marginBottom: theme.spacing(3),
  },
  statusChip: {
    fontSize: "0.75rem",
  },
  active: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactive: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  suspended: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  amountCell: {
    fontWeight: 600,
    color: theme.palette.success.main,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    minWidth: 150,
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  pagination: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2),
  },
}));

const AdminAffiliates = () => {
  const classes = useStyles();
  const history = useHistory();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);

  // Busca, filtro e os links de afiliacao cadastrados.
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("liberados");
  const [linksAfiliacao, setLinksAfiliacao] = useState([]);
  const [showLinksDialog, setShowLinksDialog] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0
  });
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    companyId: "",
    commissionRate: "10.00",
    minWithdrawAmount: "50.00",
    status: "active"
  });

  useEffect(() => {
    loadAffiliates();
    loadStats();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companiesPlan/", {
        params: { searchParam: "", pageNumber: 1 },
      });
      setCompanies(data.companies || []);
    } catch (error) {
      console.error("Error loading companies:", error);
    }
  };

  const loadAffiliates = async () => {
    try {
      setLoading(true);
      const response = await api.get("/affiliate");
      setAffiliates(response.data);
    } catch (error) {
      console.error("Error loading affiliates:", error);
      toast.error("Erro ao carregar afiliados");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get("/affiliate/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleCreateAffiliate = async () => {
    try {
      await api.post("/affiliate", {
        companyId: formData.companyId,
        enabled: true,
        paytAffiliateUrl: formData.paytAffiliateUrl || ""
      });
      toast.success("Afiliado criado com sucesso!");
      setShowCreateDialog(false);
      setFormData({
        companyId: "",
        commissionRate: "10.00",
        minWithdrawAmount: "50.00",
        status: "active"
      });
      loadAffiliates();
      loadStats();
    } catch (error) {
      console.error("Error creating affiliate:", error);
      toast.error(error.response?.data?.error || "Erro ao criar afiliado");
    }
  };

  const handleUpdateAffiliate = async () => {
    try {
      await api.put(`/affiliate/${selectedAffiliate.id}`, formData);
      toast.success("Afiliado atualizado com sucesso!");
      setShowEditDialog(false);
      setSelectedAffiliate(null);
      loadAffiliates();
      loadStats();
    } catch (error) {
      console.error("Error updating affiliate:", error);
      // O servidor recusa apelido reservado ou repetido: mostrar o motivo,
      // senao a pessoa fica tentando sem saber o que esta errado.
      toast.error(error?.response?.data?.error || "Erro ao atualizar afiliado");
    }
  };

  const handleDeleteAffiliate = async () => {
    try {
      await api.delete(`/affiliate/${selectedAffiliate.id}`);
      toast.success("Afiliado excluído com sucesso!");
      setShowDeleteDialog(false);
      setSelectedAffiliate(null);
      loadAffiliates();
      loadStats();
    } catch (error) {
      console.error("Error deleting affiliate:", error);
      toast.error("Erro ao excluir afiliado");
    }
  };

  const handleViewDetails = (affiliate) => {
    history.push(`/admin/afiliados/${affiliate.id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return classes.active;
      case "inactive":
        return classes.inactive;
      case "suspended":
        return classes.suspended;
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Ativo";
      case "inactive":
        return "Inativo";
      case "suspended":
        return "Suspenso";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const carregarLinks = async () => {
    try {
      const { data } = await api.get("/affiliate/links-afiliacao");
      setLinksAfiliacao(Array.isArray(data) ? data : []);
    } catch (e) {
      setLinksAfiliacao([]);
    }
  };

  useEffect(() => {
    carregarLinks();
  }, []);

  const salvarLinksAfiliacao = async () => {
    try {
      await api.post("/affiliate/links-afiliacao", { links: linksAfiliacao });
      toast.success("Links salvos.");
      setShowLinksDialog(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Não consegui salvar.");
    }
  };

  // O "Ativo" antigo era o status da conta. O que importa aqui e se a
  // afiliacao foi liberada.
  const afiliadosVisiveis = (affiliates || []).filter((a) => {
    if (filtro === "liberados" && !a.enabled) return false;
    if (filtro === "naoliberados" && a.enabled) return false;

    const termo = busca.trim().toLowerCase();
    if (!termo) return true;

    const alvo = [
      a.company?.name,
      a.company?.email,
      a.paytEmail,
      a.affiliateCode,
      a.slug
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return alvo.indexOf(termo) !== -1;
  });

  // Os valores vem da plataforma de pagamento; aqui e so exibicao.
  const dinheiro = (v) =>
    `R$ ${Number(v || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const openEditDialog = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setFormData({
      companyId: affiliate.companyId.toString(),
      commissionRate: affiliate.commissionRate.toString(),
      minWithdrawAmount: affiliate.minWithdrawAmount.toString(),
      status: affiliate.status,
      enabled: Boolean(affiliate.enabled),
      paytEmail: affiliate.paytEmail || "",
      slug: affiliate.slug || "",
      paytAffiliateUrl: affiliate.paytAffiliateUrl || ""
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowDeleteDialog(true);
  };

  if (loading) {
    return (
      <MainContainer>
        <Container className={classes.container}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </Box>
        </Container>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Container className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            Gerenciar Afiliados
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setShowCreateDialog(true)}
          >
            Novo Afiliado
          </Button>
        </div>

        {/* Estatísticas */}
        <div className={classes.statsContainer}>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statValue}>{stats.liberados || 0}</div>
              <div className={classes.statLabel}>Afiliados liberados</div>
            </CardContent>
          </Card>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statValue}>{stats.vendasHoje || 0}</div>
              <div className={classes.statLabel}>
                Vendas hoje · {dinheiro(stats.comissaoHoje)}
              </div>
            </CardContent>
          </Card>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statValue}>{stats.vendasMes || 0}</div>
              <div className={classes.statLabel}>
                Vendas no mês · {dinheiro(stats.comissaoMes)}
              </div>
            </CardContent>
          </Card>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statValue}>
                {dinheiro(stats.comissaoTotal)}
              </div>
              <div className={classes.statLabel}>
                Comissão do mês e de sempre · {stats.vendasTotal || 0} vendas
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela de Afiliados */}
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          style={{ gap: 12, marginBottom: 16 }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar por nome, e-mail ou código"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: 1, minWidth: 240 }}
          />

          <FormControl variant="outlined" size="small" style={{ minWidth: 190 }}>
            <Select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            >
              <MenuItem value="liberados">Afiliação liberada</MenuItem>
              <MenuItem value="naoliberados">Não liberada</MenuItem>
              <MenuItem value="todos">Todos</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowLinksDialog(true)}
          >
            Links de afiliação ({linksAfiliacao.length})
          </Button>
        </Box>

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Afiliação</TableCell>
                <TableCell>E-mail de pagamento</TableCell>
                <TableCell>Total Ganho</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {afiliadosVisiveis.map((affiliate) => (
                <TableRow key={affiliate.id}>
                  <TableCell>{affiliate.company?.name || "N/A"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                      {affiliate.affiliateCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={affiliate.enabled ? "Liberada" : "Não liberada"}
                      style={{
                        backgroundColor: affiliate.enabled ? "#10b981" : "#94a3b8",
                        color: "#fff",
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {affiliate.paytEmail || (
                      <span style={{ color: "#94a3b8" }}>— não informado</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.amountCell}>
                    R$ {parseFloat(affiliate.totalEarned || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getStatusText(affiliate.status)}
                      className={`${classes.statusChip} ${getStatusColor(affiliate.status)}`}
                    />
                  </TableCell>
                  <TableCell>{formatDate(affiliate.createdAt)}</TableCell>
                  <TableCell>
                    <div className={classes.actions}>
                      <Tooltip title="Ver Detalhes">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(affiliate)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(affiliate)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(affiliate)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {affiliates.length === 0 && (
          <Card className={classes.emptyState}>
            <CardContent>
              <Typography variant="body1" color="textSecondary">
                Nenhum afiliado encontrado.
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Clique em "Novo Afiliado" para criar o primeiro afiliado.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Dialog para Criar Afiliado */}
        <Dialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Novo Afiliado</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={formData.companyId}
                    onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                    label="Empresa"
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name} (ID: {company.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Link de afiliação</InputLabel>
                  <Select
                    value={formData.paytAffiliateUrl || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paytAffiliateUrl: e.target.value
                      })
                    }
                    label="Link de afiliação"
                  >
                    <MenuItem value="">
                      <em>Definir depois</em>
                    </MenuItem>
                    {linksAfiliacao.map((l) => (
                      <MenuItem key={l.url} value={l.url}>
                        {l.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {linksAfiliacao.length === 0 && (
                  <Typography
                    variant="caption"
                    style={{ color: "#b45309", display: "block", marginTop: 6 }}
                  >
                    Nenhum link cadastrado ainda. Use o botão "Links de
                    afiliação" para cadastrar.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box
                  p={1.5}
                  bgcolor="#ecfdf5"
                  border="1px solid #10b981"
                  borderRadius={8}
                >
                  <Typography variant="body2" style={{ color: "#065f46" }}>
                    Ao salvar, a afiliação já fica liberada: ele passa a ver o
                    painel de afiliado com o link para se cadastrar e montar a
                    página dele.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateAffiliate}
            >
              Criar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog dos links de afiliação */}
        <Dialog
          open={showLinksDialog}
          onClose={() => setShowLinksDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Links de afiliação</DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              style={{ color: "#64748b", marginBottom: 16 }}
            >
              Cadastre aqui os links da plataforma de pagamento, com um nome
              para cada faixa de comissão. Na hora de liberar um afiliado, você
              escolhe qual dar a ele.
            </Typography>

            {linksAfiliacao.map((l, i) => (
              <Box
                key={i}
                display="flex"
                style={{ gap: 8, marginBottom: 10 }}
                alignItems="flex-start"
              >
                <TextField
                  label="Nome"
                  placeholder="20% de comissão"
                  value={l.nome}
                  onChange={(e) => {
                    const copia = [...linksAfiliacao];
                    copia[i] = { ...copia[i], nome: e.target.value };
                    setLinksAfiliacao(copia);
                  }}
                  style={{ width: 180 }}
                />
                <TextField
                  label="Link"
                  placeholder="https://..."
                  value={l.url}
                  onChange={(e) => {
                    const copia = [...linksAfiliacao];
                    copia[i] = { ...copia[i], url: e.target.value.trim() };
                    setLinksAfiliacao(copia);
                  }}
                  style={{ flex: 1 }}
                />
                <Button
                  onClick={() =>
                    setLinksAfiliacao(linksAfiliacao.filter((_, j) => j !== i))
                  }
                >
                  Remover
                </Button>
              </Box>
            ))}

            <Button
              onClick={() =>
                setLinksAfiliacao([...linksAfiliacao, { nome: "", url: "" }])
              }
            >
              + Acrescentar link
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLinksDialog(false)}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={salvarLinksAfiliacao}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog para Editar Afiliado */}
        <Dialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Afiliação</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  p={2}
                  mb={1}
                  bgcolor={formData.enabled ? "#ecfdf5" : "#f8fafc"}
                  border={`1px solid ${formData.enabled ? "#10b981" : "#cbd5e1"}`}
                  borderRadius={8}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography style={{ fontWeight: 600, color: "#0f172a" }}>
                      {formData.enabled
                        ? "Afiliação liberada"
                        : "Afiliação não liberada"}
                    </Typography>
                    <Typography variant="caption" style={{ color: "#64748b" }}>
                      {formData.enabled
                        ? "Ele vê o painel de afiliado e as vendas dele."
                        : "Ele não vê o painel de afiliado."}
                    </Typography>
                  </Box>
                  <Button
                    variant={formData.enabled ? "outlined" : "contained"}
                    color="primary"
                    onClick={() =>
                      setFormData({ ...formData, enabled: !formData.enabled })
                    }
                  >
                    {formData.enabled ? "Tirar acesso" : "Liberar"}
                  </Button>
                </Box>
              </Grid>


              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Link de afiliação</InputLabel>
                  <Select
                    value={formData.paytAffiliateUrl || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paytAffiliateUrl: e.target.value
                      })
                    }
                    label="Link de afiliação"
                  >
                    <MenuItem value="">
                      <em>Nenhum por enquanto</em>
                    </MenuItem>
                    {linksAfiliacao.map((l) => (
                      <MenuItem key={l.url} value={l.url}>
                        {l.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Apelido da página dele"
                  placeholder="marlon"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      slug: e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, "")
                    })
                  }
                  helperText={
                    formData.slug
                      ? `A página dele fica em app.floow.chat/p/${formData.slug}`
                      : "Deixe vazio se ele ainda não tem página própria."
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateAffiliate}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog para Excluir Afiliado */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja excluir o afiliado "{selectedAffiliate?.company?.name}"?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteAffiliate}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainContainer>
  );
};

export default AdminAffiliates;
